import { useNavigate } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useQuery } from '@tanstack/react-query';
import type { ArticleCollection } from '@/types/article-collection';
import { findFirstArticle, buildItemTree } from '@/types/article-collection';
import { articleCollectionQueryOptions } from '@/queries/article-collections';
import { useUserTier } from '@/queries/subscriptions';
import FolderIcon from '@/assets/common/folder.svg?react';
import FileTextIcon from '@/assets/common/file-text.svg?react';
import EditIcon from '@/assets/common/edit.svg?react';
import TrashIcon from '@/assets/common/trash.svg?react';
import styles from './CollectionCard.module.scss';

interface CollectionCardProps {
  collection: ArticleCollection;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function CollectionCard({
  collection,
  onEdit,
  onDelete,
}: CollectionCardProps) {
  const navigate = useNavigate();
  const userTier = useUserTier();
  const hasReadingModeAccess = userTier !== 'foundation';

  // Fetch collection data to get the first article when collection has articles
  const { data: collectionData } = useQuery({
    ...articleCollectionQueryOptions(collection.id),
    enabled: collection.article_count > 0,
  });

  // Handle click on the card - opens first article if available
  const handleClick = () => {
    if (collectionData?.items && collectionData.items.length > 0) {
      const tree = buildItemTree(collectionData.items);
      const firstArticle = findFirstArticle(tree);
      if (firstArticle?.article_id && hasReadingModeAccess) {
        navigate({
          to: '/articles/collections/$collectionId/articles/$articleId',
          params: { collectionId: collection.id, articleId: firstArticle.article_id },
        });
        return;
      } else if (firstArticle?.article_id) {
        navigate({
          to: '/articles/$articleId',
          params: { articleId: firstArticle.article_id },
        });
        return;
      }
    }
    // No articles, go to collection management view
    navigate({
      to: '/articles/collections/$collectionId' as string,
      params: { collectionId: collection.id } as Record<string, string>,
    });
  };

  // Edit button navigates to collection management view
  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit();
    } else {
      navigate({
        to: '/articles/collections/$collectionId' as string,
        params: { collectionId: collection.id } as Record<string, string>,
      });
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.();
  };

  return (
    <motion.div
      className={styles.collectionCard}
      onClick={handleClick}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
    >
      <div className={styles.collectionCard__icon}>
        <FolderIcon />
      </div>

      <div className={styles.collectionCard__content}>
        <h3 className={styles.collectionCard__name}>{collection.name}</h3>
        <div className={styles.collectionCard__meta}>
          <span className={styles.collectionCard__stat}>
            <FileTextIcon />
            {collection.article_count} article{collection.article_count !== 1 ? 's' : ''}
          </span>
        </div>
        {collection.description && (
          <p className={styles.collectionCard__description}>
            {collection.description}
          </p>
        )}
      </div>

      <div className={styles.collectionCard__actions}>
        <button
          className={styles.collectionCard__actionButton}
          onClick={handleEditClick}
          title="Edit collection contents"
        >
          <EditIcon />
        </button>
        {onDelete && (
          <button
            className={`${styles.collectionCard__actionButton} ${styles['collectionCard__actionButton--danger']}`}
            onClick={handleDeleteClick}
            title="Delete collection"
          >
            <TrashIcon />
          </button>
        )}
      </div>
    </motion.div>
  );
}
